// Просмотр батча «как лента Reels» (DESIGN §6): ролики на весь экран, листаешь и отмечаешь
// «оставить» (отобран) или «убрать» (отклонён). Клавиши: ↑/↓ — листать, Enter — оставить, Delete — убрать, Esc — выйти.
import { Chip, IconButton, toast } from '@autoaff/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Check, ChevronDown, ChevronUp, ExternalLink, Volume2, VolumeX, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api';
import { type CreativeRow, mediaUrl, statusOf } from './data';

export function ReviewMode({ list, onClose }: { list: CreativeRow[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [i, setI] = useState(0);
  const [muted, setMuted] = useState(false);
  const [marks, setMarks] = useState<Record<number, 'approved' | 'rejected'>>({});
  const refs = useRef<Array<HTMLElement | null>>([]);
  const items = list.filter((c) => c.version);
  const cur = items[i];

  const go = useCallback(
    (n: number) => {
      const k = Math.max(0, Math.min(items.length - 1, n));
      refs.current[k]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [items.length],
  );

  const mark = useCallback(
    async (c: CreativeRow, status: 'approved' | 'rejected') => {
      setMarks((m) => ({ ...m, [c.id]: status }));
      try {
        await api(`/api/creatives/${c.id}`, { status }, 'PATCH');
        void qc.invalidateQueries({ queryKey: ['creatives'] });
      } catch (err) {
        toast((err as Error).message);
      }
      go(items.indexOf(c) + 1);
    },
    [go, items, qc],
  );

  // Какой ролик на экране — тот и играет
  useEffect(() => {
    const io = new IntersectionObserver(
      (es) => {
        for (const e of es) {
          const v = e.target.querySelector('video');
          const k = Number((e.target as HTMLElement).dataset.k);
          if (e.isIntersecting && e.intersectionRatio > 0.6) {
            setI(k);
            if (v) {
              v.currentTime = 0;
              // Телефон не даёт играть со звуком без касания — тогда дальше без звука, кнопка звука видна
              void v.play().catch((err: unknown) => {
                if ((err as { name?: string } | null)?.name !== 'NotAllowedError') return;
                v.muted = true;
                setMuted(true);
                void v.play().catch(() => {});
              });
            }
          } else v?.pause();
        }
      },
      { threshold: [0, 0.6, 1] },
    );
    for (const el of refs.current) if (el) io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        go(i + 1);
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        go(i - 1);
      } else if (e.key === 'Enter' && cur) void mark(cur, 'approved');
      else if ((e.key === 'Delete' || e.key === 'Backspace') && cur) void mark(cur, 'rejected');
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [go, i, cur, mark, onClose]);

  const kept = Object.values(marks).filter((m) => m === 'approved').length;
  return createPortal(
    <div className="reels" role="dialog" aria-modal="true" aria-label="Просмотр роликов">
      <header className="reels-head">
        <span>
          {i + 1} из {items.length}
          {kept ? ` · оставлено ${kept}` : ''}
        </span>
        <div className="reels-head-b">
          <IconButton label={muted ? 'Включить звук' : 'Выключить звук'} onClick={() => setMuted((m) => !m)}>
            {muted ? <VolumeX /> : <Volume2 />}
          </IconButton>
          <IconButton label="Закрыть" onClick={onClose}>
            <X />
          </IconButton>
        </div>
      </header>
      <div className="reels-feed">
        {items.map((c, k) => {
          const m = marks[c.id];
          const st = statusOf(m ?? c.status);
          return (
            <section
              key={c.id}
              data-k={k}
              ref={(el) => {
                refs.current[k] = el;
              }}
              className="reel"
            >
              <div className="reel-frame">
                <video
                  src={mediaUrl(c.version?.video) ?? undefined}
                  poster={mediaUrl(c.version?.poster) ?? undefined}
                  muted={muted}
                  loop
                  playsInline
                  preload={Math.abs(k - i) <= 1 ? 'auto' : 'none'}
                  onClick={(e) => {
                    const v = e.currentTarget;
                    if (v.paused) void v.play();
                    else v.pause();
                  }}
                />
                <div className="reel-info">
                  <span className="reel-chips">
                    <Chip tone={st.tone} dot>
                      {st.label}
                    </Chip>
                    {c.fatigue && !m && (
                      <Chip tone="warning" dot>
                        Выгорает
                      </Chip>
                    )}
                  </span>
                  <b>
                    {c.offerName} · {c.code}
                  </b>
                  <span>{c.angle ?? c.hook ?? ''}</span>
                  <Link
                    to="/creatives/$id"
                    params={{ id: String(c.id) }}
                    className="reel-open"
                    onClick={onClose}
                  >
                    Открыть ролик <ExternalLink aria-hidden />
                  </Link>
                </div>
              </div>
              <div className="reel-actions">
                <button
                  type="button"
                  className={`reel-btn no${m === 'rejected' ? ' on' : ''}`}
                  onClick={() => void mark(c, 'rejected')}
                >
                  <X aria-hidden />
                  Отклонить
                </button>
                <button
                  type="button"
                  className={`reel-btn yes${m === 'approved' ? ' on' : ''}`}
                  onClick={() => void mark(c, 'approved')}
                >
                  <Check aria-hidden />В рекламу
                </button>
              </div>
            </section>
          );
        })}
      </div>
      <div className="reels-nav hide-m">
        <IconButton label="Предыдущий" onClick={() => go(i - 1)} disabled={i === 0}>
          <ChevronUp />
        </IconButton>
        <IconButton label="Следующий" onClick={() => go(i + 1)} disabled={i >= items.length - 1}>
          <ChevronDown />
        </IconButton>
      </div>
    </div>,
    document.body,
  );
}
